import {
  ColDef,
  DefaultMenuItem,
  IServerSideGetRowsParams,
  MenuItemDef,
  SortModelItem,
  ValueFormatterParams,
} from "ag-grid-enterprise";
import { PropsWithChildren, createContext, useCallback, useState } from "react";
import useAuth from "src/hooks/useAuth";
import useContact from "src/hooks/useContact";
import useReference from "src/hooks/useReference";
import { ReportInputs } from "src/models/ReportInputs";
import { ReportRequest, SortModel } from "src/models/ReportRequest";
import { ReportToggles } from "src/models/ReportToggles";
import { formatCurrency } from "src/utils/FormatCurrency";
import { toDateOnlyISO } from "src/utils/DateUtils";

const initialReportInputs: ReportInputs = {
  fromDate: new Date(),
  toDate: new Date(),
  programId: undefined,
  merchantGroups: [],
  locations: [],
  csr: null,
  reportGroupId: "",
  campaignId: "",
  category: undefined,
  icaValue: undefined,
  binValue: undefined,
  icaList: [],
  binList: [],
  programIds: [],
  fundingTypeId: undefined,
  countryValue: undefined,
  countryList: [],
  reportToggles: {groupByIca:false, groupByBin:false, groupByProgram: true, groupByDate: true, showSubtotals: false },
  banks: null,
  adjustmentType: undefined,
  emlReasonCodeId: undefined,
  portfolioId: undefined,
  lastName: undefined,
  firstName: undefined,
  fromBin: undefined,
  toBin: undefined,
  authRequestDeclineReason: undefined,
  
  developerId: null
};

export type ReportProviderContextType = {
  reportInputs: ReportInputs;
  setReportInputs: React.Dispatch<React.SetStateAction<ReportInputs>>;
  initializeReportInputs: (category: string | undefined) => void;
  exceedsMaxDateRange: boolean;
  setExceedsMaxDateRange: React.Dispatch<React.SetStateAction<boolean>>;
  mapReportRequest: (params: IServerSideGetRowsParams | null) => ReportRequest;
  currencyFormatter: (params: ValueFormatterParams) => string;
  dateFormatter: (params: ValueFormatterParams) => string;
  timeFormatter: (params: ValueFormatterParams) => string;
  defaultColDef: ColDef;
  getContextMenuItems: () => (DefaultMenuItem | MenuItemDef<any, any>)[];
  cardNumberFormatter: (params: ValueFormatterParams) => string;
};

const ReportContext = createContext({} as ReportProviderContextType);

export const ReportProvider = ({ children }: PropsWithChildren) => {
  const { currencyList } = useReference();
  const { bankIds } = useContact();
  const { hasClaim } = useAuth();
  const [reportInputs, setReportInputs] =
    useState<ReportInputs>(initialReportInputs);
  const [exceedsMaxDateRange, setExceedsMaxDateRange] = useState(false);

  const initializeReportInputs = (category?: string) => {
    const fromDate = reportInputs.fromDate;
    const toDate = reportInputs.toDate;
    const initReportToggles: ReportToggles = {
      groupByIca: reportInputs?.reportToggles?.groupByIca ?? false,
      groupByBin: reportInputs?.reportToggles?.groupByBin ?? false,
      groupByProgram: reportInputs?.reportToggles?.groupByProgram ?? true,
      groupByDate: reportInputs?.reportToggles?.groupByDate ?? true,
      showSubtotals: reportInputs?.reportToggles?.showSubtotals ?? false
    }
    setReportInputs({
      ...reportInputs,
      fromDate: fromDate ?? new Date(),
      toDate: toDate ?? new Date(),
      programId: reportInputs?.programId,
      merchantGroups: [],
      locations: [],
      csr: null,
      reportGroupId: "",
      campaignId: "",
      category: category,      
      icaValue: "",
      binValue: "",
      programIds: [],
      fundingTypeId: "",
      reportToggles: initReportToggles,
      banks: "",
      countryValue: "",
      adjustmentType: "",
      emlReasonCodeId: reportInputs?.emlReasonCodeId,
      portfolioId: reportInputs?.portfolioId,
      lastName: reportInputs?.lastName,
      firstName: reportInputs?.firstName,
      fromBin: reportInputs?.fromBin,
      toBin: reportInputs?.toBin,
      authRequestDeclineReason: reportInputs?.authRequestDeclineReason,
      
      developerId: ""
    });
    setExceedsMaxDateRange(false);
  };

  const mapSortModel = (sortModel: SortModelItem[] | undefined) => {
    if (!sortModel || (sortModel?.length ?? 0) < 1) return undefined;
    const sort: SortModel = {
      colId: sortModel[0]?.colId,
      sort: sortModel[0]?.sort,
    };
    return sort;
  };

  const mapReportRequest = (
    params: IServerSideGetRowsParams | null
  ): ReportRequest => {
    const mgs = reportInputs?.merchantGroups?.join();
    const locs = reportInputs?.locations?.join();
    const icas = reportInputs?.icaList?.join();
    const bins = reportInputs?.binList?.join();
    const programs = reportInputs?.programIds?.join();
    const countries = reportInputs?.countryList?.join();

    const request: ReportRequest = {
      startRow: params?.request?.startRow || 0,
      endRow: params?.request?.endRow || 50,
      sort: mapSortModel(params?.request?.sortModel),
      fromDate: toDateOnlyISO(reportInputs?.fromDate),
      toDate: toDateOnlyISO(reportInputs?.toDate),
      programId: reportInputs?.programId ?? -1,
      merchantGroups: (mgs?.length ?? 0) > 0 ? mgs : null,
      resultInLocalTime: Boolean(reportInputs?.resultInLocalTime),
      locations: (locs?.length ?? 0) > 0 ? locs : null,
      csr: (reportInputs?.csr?.length ?? 0) === 0 ? null : reportInputs.csr,
      reportGroupId:
        reportInputs?.reportGroupId?.length === 0
          ? null
          : reportInputs.reportGroupId,
      campaignId:
        reportInputs?.campaignId?.length === 0 ? null : reportInputs.campaignId,
      category: reportInputs.category,
      icaValue: reportInputs?.icaValue?.length === 0 ? null : reportInputs.icaValue,
      binValue: reportInputs?.binValue?.length === 0 ? null : reportInputs.binValue,
      icaList: (icas?.length ?? 0) > 0 ? icas : null,
      binList:  (bins?.length ?? 0) > 0 ? bins : null,
      programIds: (programs?.length ?? 0) > 0 ? programs: null,
      fundingTypeId:
        (reportInputs?.fundingTypeId?.length ?? 0) === 0
          ? null
          : reportInputs.fundingTypeId,
      adjustmentType:
          (reportInputs?.adjustmentType?.length ?? 0) === 0
              ? null
              : reportInputs.adjustmentType,
      emlReasonCodeId: (reportInputs?.emlReasonCodeId?.length ?? 0) === 0
            ? null
            : reportInputs.emlReasonCodeId,  
      portfolioId: (reportInputs?.portfolioId?.length ?? 0) === 0
            ? null
            : reportInputs.portfolioId,
      lastName: (reportInputs?.lastName?.length ?? 0) === 0
          ? null
          : reportInputs.lastName,
      firstName: (reportInputs?.firstName?.length ?? 0) === 0
          ? null
          : reportInputs.firstName,
      fromBin: (reportInputs?.fromBin?.length ?? 0) === 0
          ? null
          : reportInputs.fromBin,
      toBin: (reportInputs?.toBin?.length ?? 0) === 0
          ? null
            : reportInputs.toBin,
      authRequestDeclineReason: (reportInputs?.authRequestDeclineReason?.length ?? 0) === 0
            ? null
            : reportInputs.authRequestDeclineReason,
      reportToggles: reportInputs?.reportToggles,
      banks: (bankIds?.length ?? 0) > 0 ? bankIds : null,
      countryValue: reportInputs?.countryValue?.length === 0 ? null : reportInputs.countryValue,
      countryList: (countries?.length ?? 0) > 0 ? countries : null,
      developerId: reportInputs?.developerId?.length === 0 ? null : reportInputs.developerId,
    };
    return request;
  };

  const currencyFormatter = (params: ValueFormatterParams) => {
    if (params.value == undefined || params.data == undefined) return "";
    const alphaCode =
      currencyList.find((c) => c.numericCode === params.data.currencyCode)
        ?.alphaCode ?? "";
    const cur = formatCurrency(params.value, alphaCode);
    return cur + "";
  };

  const dateFormatter = (params: ValueFormatterParams) => {
    if (params.value == undefined || params.data == undefined) return "";
    if (params.data.transactionTime == undefined) return "";
    return new Date(params.data.transactionTime).toLocaleDateString();
  };

  const timeFormatter = (params: ValueFormatterParams) => {
    if (params.value == undefined || params.data == undefined) return "";
    if (params.data.transactionTime == undefined) return "";
    return new Date(params.data.transactionTime).toLocaleTimeString();
  };

  const cardNumberFormatter = (params: ValueFormatterParams) => {    
    const fullCardNumber = params.value;
    if (params.value == undefined || params.data == undefined) return "";
    if (fullCardNumber.Length <= 4) return fullCardNumber;

    if (hasClaim("ViewRestrictedCardData")) return fullCardNumber;

    const strVal = String(fullCardNumber);

    const unmaskedPart = strVal.slice(-4);
    const maskedPart = "*".repeat(strVal.length - 4);

    return maskedPart + unmaskedPart;
  };

  const defaultColDef: ColDef = {
    sortable: true,
    filter: false,
    cellStyle: { textAlign: "center" },
  };

  const getContextMenuItems = useCallback(() => {
    const result: (DefaultMenuItem | MenuItemDef)[] = [
      "copy",
      "copyWithHeaders",
      "separator",
      "chartRange",
    ];
    return result;
  }, []);

  return (
    <ReportContext.Provider
      value={{
        reportInputs,
        setReportInputs,
        initializeReportInputs,
        exceedsMaxDateRange,
        setExceedsMaxDateRange,
        mapReportRequest,
        currencyFormatter,
        dateFormatter,
        timeFormatter,
        defaultColDef,
        getContextMenuItems,
        cardNumberFormatter
      }}
    >
      {children}
    </ReportContext.Provider>
  );
};

export default ReportContext;
